/**
 * Flagged Messages Management Hook
 * 
 * Provides administrative access to messages that have been flagged
 * for containing contact information. The system now allows messages through
 * after user acknowledgement but logs them for admin visibility.
 * 
 * **Detection System:**
 * The platform automatically scans all job messages for patterns indicating
 * contact information sharing:
 * - Email addresses (regex patterns)
 * - Phone numbers (various formats)
 * - URLs with contact info
 * - Social media handles
 * - Obfuscated contact info ("call me at...")
 * 
 * **Flagged Message Lifecycle:**
 * 1. User attempts to send message with contact info
 * 2. System detects contact information
 * 3. User sees warning about off-platform communication
 * 4. User can choose to send anyway or edit
 * 5. If sent, message is logged for admin review
 * 6. Stats track sharing patterns
 * 
 * **Analytics Features:**
 * - Total flagged message count
 * - Breakdown by detection type
 * - Frequent sharers (users sharing most contact info)
 * - Recent trends (30-day chart)
 * - CSV export for compliance reporting
 * 
 * @returns {Object} Flagged messages management functions
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface FlaggedMessage {
  id: string;
  user_id: string;
  organization_id: string;
  job_id: string;
  message_content: string;
  detection_type: string;
  flagged_content: string;
  user_role: string;
  created_at: string;
  user_agent?: string;
  ip_address?: string | null;
  message_sent: boolean;
  // Joined data
  user_email?: string;
  organization_name?: string;
  job_title?: string;
  job_public_id?: string;
}

export interface FlaggedMessageStats {
  totalFlagged: number;
  byDetectionType: Record<string, number>;
  frequentSharers: Array<{
    user_id: string;
    user_email: string;
    share_count: number;
  }>;
  recentTrends: Array<{
    date: string;
    count: number;
  }>;
}

export function useFlaggedMessages() {
  const [flaggedMessages, setFlaggedMessages] = useState<FlaggedMessage[]>([]);
  const [stats, setStats] = useState<FlaggedMessageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFlaggedMessages = async (filters?: {
    startDate?: string;
    endDate?: string;
    detectionType?: string;
    userId?: string;
    organizationId?: string;
  }) => {
    try {
      setLoading(true);
      
      // First verify we have admin access
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user) {
        throw new Error('User not authenticated');
      }

      let query = supabase
        .from('flagged_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate);
      }
      
      if (filters?.detectionType) {
        query = query.eq('detection_type', filters.detectionType);
      }
      
      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }
      
      if (filters?.organizationId) {
        query = query.eq('organization_id', filters.organizationId);
      }

      console.log('Fetching flagged messages for user:', currentUser.user.id);
      const { data, error: fetchError } = await query.limit(1000);

      if (fetchError) {
        console.error('Error fetching flagged messages:', fetchError);
        throw fetchError;
      }

      console.log('Fetched flagged messages:', data?.length || 0);

      // Get user emails from profiles and their auth metadata
      let emailMap = new Map();
      
      try {
        const { data: userEmails, error: emailError } = await supabase
          .rpc('get_all_users_for_admin');
        
        if (!emailError && userEmails) {
          emailMap = new Map(userEmails.map((user: any) => [user.id, user.email]));
        }
      } catch (error) {
        console.warn('Could not fetch user emails from admin function, falling back to profiles');
        
        // Fallback: get emails from profiles with auth data
        const userIds = [...new Set(data?.map((msg: any) => msg.user_id) || [])];
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id')
            .in('user_id', userIds);
          
          // For now, use user IDs as fallback emails until we can get proper emails
          profiles?.forEach((profile: any) => {
            emailMap.set(profile.user_id, `user-${profile.user_id.slice(0, 8)}@domain.com`);
          });
        }
      }

      // Get organizations and jobs info
      const orgIds = [...new Set(data?.map((msg: any) => msg.organization_id) || [])];
      const jobIds = [...new Set(data?.map((msg: any) => msg.job_id) || [])];

      const [orgsResult, jobsResult] = await Promise.all([
        supabase.from('organizations').select('id, name').in('id', orgIds),
        supabase.from('jobs').select('id, title, public_id').in('id', jobIds)
      ]);

      // Create lookup maps
      const orgMap = new Map(orgsResult.data?.map((org: any) => [org.id, org.name]) || []);
      const jobMap = new Map(jobsResult.data?.map((job: any) => [job.id, { title: job.title, public_id: job.public_id }]) || []);

      // Enhance data with email and organization info
      const enhancedData: FlaggedMessage[] = (data || []).map((msg: any) => {
        const jobInfo = jobMap.get(msg.job_id);
        return {
          ...msg,
          ip_address: msg.ip_address as string | null,
          user_email: emailMap.get(msg.user_id) || 'Unknown',
          organization_name: orgMap.get(msg.organization_id) || 'Unknown',
          job_title: jobInfo?.title || 'Unknown',
          job_public_id: jobInfo?.public_id || 'Unknown'
        };
      });

      setFlaggedMessages(enhancedData);
    } catch (err) {
      console.error('Error fetching flagged messages:', err);
      if (err instanceof Error) {
        setError(`Failed to fetch flagged messages: ${err.message}`);
      } else {
        setError('Failed to fetch flagged messages');
      }
      // Set empty array so component doesn't show loading indefinitely
      setFlaggedMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Get total count and stats
      const { data: allMessages, error: statsError } = await supabase
        .from('flagged_messages')
        .select(`
          detection_type,
          user_id,
          created_at
        `);

      if (statsError) throw statsError;

      if (!allMessages) {
        setStats({
          totalFlagged: 0,
          byDetectionType: {},
          frequentSharers: [],
          recentTrends: []
        });
        return;
      }

      // Calculate detection type distribution
      const byDetectionType = allMessages.reduce((acc: Record<string, number>, msg: any) => {
        acc[msg.detection_type] = (acc[msg.detection_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Calculate frequent sharers
      const userShares = allMessages.reduce((acc: Record<string, number>, msg: any) => {
        acc[msg.user_id] = (acc[msg.user_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Get user emails for frequent sharers
      let emailMap = new Map();
      try {
        const { data: userEmails } = await supabase.rpc('get_all_users_for_admin');
        if (userEmails) {
          emailMap = new Map(userEmails.map((user: any) => [user.id, user.email]));
        }
      } catch (error) {
        console.warn('Could not fetch emails for stats, using fallback');
      }

      const frequentSharers = Object.entries(userShares)
        .sort(([, a], [, b]) => (b as number) - (a as number))
        .slice(0, 10)
        .map(([userId, count]) => ({
          user_id: userId,
          user_email: emailMap.get(userId) || `user-${userId.slice(0, 8)}@hidden.com`,
          share_count: count as number
        }));

      // Calculate recent trends (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentMessages = allMessages.filter((msg: any) => 
        new Date(msg.created_at) >= thirtyDaysAgo
      );

      const recentTrends = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(thirtyDaysAgo);
        date.setDate(date.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        
        const count = recentMessages.filter((msg: any) => 
          msg.created_at.startsWith(dateStr)
        ).length;
        
        return { date: dateStr, count };
      });

      setStats({
        totalFlagged: allMessages.length,
        byDetectionType,
        frequentSharers,
        recentTrends
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const exportToCSV = () => {
    if (flaggedMessages.length === 0) return;

    const headers = [
      'Date',
      'User Email',
      'Organization',
      'Job Title',
      'Job ID',
      'Detection Type',
      'Flagged Content',
      'User Role',
      'Message Sent',
      'Partial Message'
    ];

    const csvData = flaggedMessages.map(msg => [
      new Date(msg.created_at).toLocaleString(),
      msg.user_email || 'Unknown',
      msg.organization_name || 'Unknown',
      msg.job_title || 'Unknown',
      msg.job_public_id || 'Unknown',
      msg.detection_type,
      msg.flagged_content,
      msg.user_role,
      msg.message_sent ? 'Yes' : 'No',
      msg.message_content.substring(0, 100) + (msg.message_content.length > 100 ? '...' : '')
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `flagged_messages_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchFlaggedMessages();
    fetchStats();
  }, []);

  return {
    flaggedMessages,
    stats,
    loading,
    error,
    fetchFlaggedMessages,
    fetchStats,
    exportToCSV,
    refetch: () => {
      fetchFlaggedMessages();
      fetchStats();
    }
  };
}
